import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';

interface StartupApplicationFormProps {
  startupId: string;
  roleId: string;
  onSuccess: () => void;
}

export const StartupApplicationForm = ({ startupId, roleId, onSuccess }: StartupApplicationFormProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [message, setMessage] = useState('');
  const [experience, setExperience] = useState('');
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!user || !message.trim() || !experience.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let cvUrl = null;

      if (cvFile) {
        const fileExt = cvFile.name.split('.').pop();
        const filePath = `${crypto.randomUUID()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('applications')
          .upload(filePath, cvFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('applications')
          .getPublicUrl(filePath);

        cvUrl = publicUrl;
      }

      const { error } = await supabase
        .from('startup_applications')
        .insert({
          startup_id: startupId,
          role_id: roleId,
          user_id: user.id,
          message,
          experience,
          cv_url: cvUrl
        });

      if (error) throw error;

      toast({
        title: "Application submitted",
        description: "Your application has been submitted successfully.",
      });

      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="message">Why do you want to join?</Label>
        <Textarea
          id="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="mt-1"
          placeholder="Tell us why you're interested in this position..."
        />
      </div>

      <div>
        <Label htmlFor="experience">Relevant Experience</Label>
        <Textarea
          id="experience"
          value={experience}
          onChange={(e) => setExperience(e.target.value)}
          className="mt-1"
          placeholder="Share your relevant experience..."
        />
      </div>

      <div>
        <Label htmlFor="cv">CV (Optional)</Label>
        <Input
          id="cv"
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={(e) => setCvFile(e.target.files?.[0] || null)}
          className="mt-1"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={isSubmitting}
        className="w-full bg-flux-cyan hover:bg-flux-cyan/80"
      >
        {isSubmitting ? "Submitting..." : "Submit Application"}
      </Button>
    </div>
  );
};